import { useState } from 'react';
import { Envelope, ShieldCheck, UserPlus, UsersThree, Warning } from '@phosphor-icons/react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/layout/EmptyState';
import { useEmployees, type NeuraxEmployee } from '@/lib/employees/useEmployees';
import { useAuth } from '@/lib/auth/AuthProvider';
import { InviteEmployeeDialog } from '@/components/employees/InviteEmployeeDialog';
import { cn } from '@/lib/utils/cn';

export default function EmployeesPage() {
  const { isAdmin } = useAuth();
  const { data, isLoading, isError, error } = useEmployees();
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <>
      <PageHeader
        eyebrow="Team"
        title="Employees"
        description="Invite teammates, manage roles, and control who can issue NeuraX licenses."
        actions={
          isAdmin ? (
            <button
              data-testid="invite-employee-btn"
              onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110"
            >
              <UserPlus className="h-4 w-4" />
              Invite teammate
            </button>
          ) : null
        }
      />

      {isError && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <Warning className="mt-0.5 h-4 w-4" />
          <span>{(error as Error)?.message || 'Failed to load employees.'}</span>
        </div>
      )}

      <div className="glass-panel overflow-hidden">
        <div className="grid grid-cols-12 items-center gap-4 border-b border-white/5 px-6 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-6 sm:col-span-5">Member</div>
          <div className="col-span-3 sm:col-span-3">Role</div>
          <div className="col-span-3 sm:col-span-2">Status</div>
          <div className="hidden sm:col-span-2 sm:block text-right">Joined</div>
        </div>

        {isLoading && (
          <div className="divide-y divide-white/5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                data-testid="employee-skeleton"
                className="grid grid-cols-12 items-center gap-4 px-6 py-4"
              >
                <div className="col-span-6 sm:col-span-5 flex items-center gap-3">
                  <div className="h-9 w-9 animate-pulse rounded-full bg-white/5" />
                  <div className="space-y-2">
                    <div className="h-3 w-32 animate-pulse rounded bg-white/5" />
                    <div className="h-2.5 w-44 animate-pulse rounded bg-white/5" />
                  </div>
                </div>
                <div className="col-span-3 h-3 w-16 animate-pulse rounded bg-white/5" />
                <div className="col-span-3 sm:col-span-2 h-3 w-16 animate-pulse rounded bg-white/5" />
                <div className="hidden sm:block sm:col-span-2 h-3 w-12 animate-pulse rounded bg-white/5 ml-auto" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && (data?.length ?? 0) === 0 && (
          <div data-testid="employees-empty" className="px-6 py-12">
            <EmptyState
              title="No teammates yet"
              description="Invite your first teammate to start issuing NeuraX licenses."
            />
          </div>
        )}

        {!isLoading && (data?.length ?? 0) > 0 && (
          <div className="divide-y divide-white/5">
            {data!.map((emp) => (
              <EmployeeRow key={emp.id} emp={emp} />
            ))}
          </div>
        )}
      </div>

      {inviteOpen && <InviteEmployeeDialog open onClose={() => setInviteOpen(false)} />}
    </>
  );
}

function EmployeeRow({ emp }: { emp: NeuraxEmployee }) {
  const initials = (emp.full_name || emp.email)
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div
      data-testid={`employee-row-${emp.id}`}
      className="grid grid-cols-12 items-center gap-4 px-6 py-4"
    >
      <div className="col-span-6 sm:col-span-5 flex items-center gap-3 min-w-0">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-neurax-electric/40 to-neurax-plasma/40 text-xs font-semibold text-white">
          {initials || <UsersThree className="h-4 w-4" />}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-white">{emp.full_name || emp.email}</p>
          <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
            <Envelope className="h-3 w-3" />
            {emp.email}
          </p>
        </div>
      </div>
      <div className="col-span-3">
        <RoleBadge role={emp.role} />
      </div>
      <div className="col-span-3 sm:col-span-2">
        <InviteBadge status={emp.invite_status} />
      </div>
      <div className="hidden sm:col-span-2 sm:block text-right text-xs text-muted-foreground">
        {new Date(emp.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
      </div>
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const label = role === 'team_lead' ? 'Team Lead' : role.charAt(0).toUpperCase() + role.slice(1);
  const style =
    role === 'admin'
      ? 'border-neurax-amber/30 bg-neurax-amber/10 text-neurax-amber'
      : role === 'team_lead'
        ? 'border-neurax-plasma/30 bg-neurax-plasma/10 text-neurax-plasma'
        : role === 'recruiter'
          ? 'border-neurax-electric/30 bg-neurax-electric/10 text-neurax-electric'
          : 'border-white/10 bg-white/[0.04] text-muted-foreground';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium uppercase tracking-wider',
        style
      )}
    >
      {role === 'admin' && <ShieldCheck className="h-3 w-3" />}
      {label}
    </span>
  );
}

function InviteBadge({ status }: { status: string | null }) {
  if (!status || status === 'accepted') {
    return (
      <span className="inline-flex items-center rounded-full border border-neurax-mint/30 bg-neurax-mint/10 px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-neurax-mint">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-neurax-amber/30 bg-neurax-amber/10 px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-neurax-amber">
      {status}
    </span>
  );
}
